import React, { useRef, useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Search } from 'lucide-react';
import { useAuth } from '../AuthContext';
import { useLanguage } from '../LanguageContext';
import { useTheme } from '../ThemeContext';
import api from '../api';
import './Navbar.css';

const HOVER_CLOSE_DELAY = 240;
const THEME_LABELS = {
  en: {
    forest: 'Forest',
    mint: 'Mint',
    graphite: 'Graphite',
  },
  zh: {
    forest: '森林',
    mint: '薄荷',
    graphite: '石墨',
  },
};
const THEME_SWATCHES = {
  forest: 'var(--primary)',
  mint: '#16a085',
  graphite: '#5f6973',
};

function createMenuConfig(t, isLoggedIn, language) {
  const labels = language === 'zh' ? {
    development: '开发',
    webDevelopment: '网站开发',
    mobileDevelopment: '移动端开发',
    automation: '自动化',
    design: '设计',
    uiux: 'UI/UX 设计',
    brandDesign: '品牌设计',
    writing: '写作',
    serviceCatalog: '服务目录',
    postRequirement: '发布需求',
    financeCenter: '财务中心',
    browseRequirements: '需求大厅',
    savedServices: '已收藏服务',
    talentGuides: '人才指南',
    howItWorks: '如何运作',
    trustSafety: '信任与安全',
    bestMatches: '最佳匹配',
    latestServices: '最新服务',
    categories: '分类',
    lifestyle: '生活服务',
    myRequirements: '我的需求',
    myServices: '我的服务',
    createRequirement: '发布需求',
    expertDirectory: '专家目录',
  } : {
    development: 'Development',
    webDevelopment: 'Web Development',
    mobileDevelopment: 'Mobile Development',
    automation: 'Automation',
    design: 'Design',
    uiux: 'UI/UX Design',
    brandDesign: 'Brand Design',
    writing: 'Writing',
    serviceCatalog: 'Service Catalog',
    postRequirement: 'Post a Requirement',
    financeCenter: 'Finance Center',
    browseRequirements: 'Requirement Hall',
    savedServices: 'Saved Services',
    talentGuides: 'Talent Guides',
    howItWorks: 'How It Works',
    trustSafety: 'Trust & Safety',
    bestMatches: 'Best Matches',
    latestServices: 'Latest Services',
    categories: 'Categories',
    lifestyle: 'Lifestyle',
    myRequirements: 'My Requirements',
    myServices: 'My Services',
    createRequirement: 'Post Requirement',
    expertDirectory: 'Expert Directory',
  };

  if (!isLoggedIn) {
    return [
      {
        label: t.findServices || labels.serviceCatalog,
        to: '/services',
        children: [
          { label: labels.bestMatches, to: '/services' },
          { label: labels.latestServices, to: '/services?sort=latest' },
          { label: labels.savedServices, to: '/services?saved=1' },
        ],
      },
      {
        label: t.findExperts || labels.expertDirectory,
        to: '/freelancers',
        children: [
          { label: labels.expertDirectory, to: '/freelancers' },
          { label: labels.development, to: `/freelancers?category=${encodeURIComponent('技术服务')}` },
          { label: labels.design, to: `/freelancers?category=${encodeURIComponent('创意制作')}` },
        ],
      },
      { label: labels.postRequirement, to: '/tasks/create' },
      {
        label: t.findTasks || labels.browseRequirements,
        to: '/tasks',
        children: [
          { label: labels.browseRequirements, to: '/tasks' },
          { label: labels.createRequirement, to: '/tasks/create' },
        ],
      },
      {
        label: t.why,
        to: '/about',
        children: [
          { label: labels.howItWorks, to: '/about#how-it-works' },
          { label: labels.trustSafety, to: '/help?safety=1' },
        ],
      },
    ];
  }

  return [
    {
      label: t.findServices || (language === 'zh' ? '找服务' : 'Find Services'),
      to: '/services',
      children: [
        { label: labels.bestMatches, to: '/services' },
        { label: labels.latestServices, to: '/services?sort=latest' },
        { label: labels.savedServices, to: '/services?saved=1' },
      ],
    },
    {
      label: t.findExperts || (language === 'zh' ? '找专家' : 'Find Experts'),
      to: '/freelancers',
      children: [
        { label: labels.expertDirectory, to: '/freelancers' },
        { label: labels.development, to: `/freelancers?category=${encodeURIComponent('技术服务')}` },
        { label: labels.design, to: `/freelancers?category=${encodeURIComponent('创意制作')}` },
      ],
    },
    { label: t.publishService || (language === 'zh' ? '发布服务' : 'Post Service'), to: '/services/new' },
    { label: labels.myServices, to: '/services/mine' },
    {
      label: t.findTasks || (language === 'zh' ? '需求大厅' : 'Requirement Hall'),
      to: '/tasks',
      children: [
        { label: labels.browseRequirements, to: '/tasks' },
        { label: labels.myRequirements, to: '/requirements/mine' },
        { label: labels.createRequirement, to: '/tasks/create' },
      ],
    },
    {
      label: t.finances,
      to: '/wallet',
    },
    { label: t.messages, to: '/messages' },
  ];
}

function NavMenuItem({ item, level = 1, isMobile, openPath, setOpenPath }) {
  const closeTimer = useRef(null);
  const itemRef = useRef(null);
  const [openLeft, setOpenLeft] = useState(false);
  const hasChildren = Array.isArray(item.children) && item.children.length > 0;
  const key = `${level}:${item.label}`;
  const isOpen = openPath[level - 1] === key;

  const clearCloseTimer = () => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  };

  const openMenu = () => {
    clearCloseTimer();
    if (!hasChildren) return;
    setOpenPath(prev => {
      const next = prev.slice(0, level - 1);
      next[level - 1] = key;
      return next;
    });
    requestAnimationFrame(() => {
      const rect = itemRef.current?.getBoundingClientRect();
      setOpenLeft(Boolean(rect && rect.right + 260 > window.innerWidth));
    });
  };

  const scheduleClose = (event) => {
    if (isMobile || !hasChildren) return;
    const nextTarget = event?.relatedTarget;
    if (nextTarget instanceof Node && event.currentTarget.contains(nextTarget)) return;
    clearCloseTimer();
    closeTimer.current = setTimeout(() => {
      setOpenPath(prev => prev.slice(0, level - 1));
    }, HOVER_CLOSE_DELAY);
  };

  const handleClick = (event) => {
    if (isMobile && hasChildren) {
      event.preventDefault();
      setOpenPath(prev => {
        if (prev[level - 1] === key) return prev.slice(0, level - 1);
        const next = prev.slice(0, level - 1);
        next[level - 1] = key;
        return next;
      });
      return;
    }
    if (!hasChildren) setOpenPath([]);
  };

  const content = item.to ? (
    <Link to={item.to} className="nav-menu-trigger" onClick={handleClick}>
      <span>{item.label}</span>
      {hasChildren && <span className="nav-caret" aria-hidden="true" />}
    </Link>
  ) : (
    <button type="button" className="nav-menu-trigger" onClick={handleClick}>
      <span>{item.label}</span>
      {hasChildren && <span className="nav-caret" aria-hidden="true" />}
    </button>
  );

  return (
    <div
      ref={itemRef}
      className={`nav-menu-item nav-menu-level-${level} ${hasChildren ? 'has-children' : ''} ${isOpen ? 'open' : ''} ${openLeft ? 'open-left' : ''}`}
      onMouseEnter={!isMobile ? openMenu : undefined}
      onMouseLeave={!isMobile ? scheduleClose : undefined}
      onFocus={!isMobile ? openMenu : undefined}
    >
      {content}
      {hasChildren && isOpen && (
        <div className={`nav-submenu nav-submenu-level-${level + 1}`}>
          {item.children.map(child => (
            <NavMenuItem
              key={child.label}
              item={child}
              level={level + 1}
              isMobile={isMobile}
              openPath={openPath}
              setOpenPath={setOpenPath}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function UserAvatar({ user, className }) {
  const initial = user?.username?.[0]?.toUpperCase() || 'M';
  if (user?.avatar) {
    return (
      <div className={className}>
        <img src={user.avatar} alt="" />
      </div>
    );
  }
  return <div className={className}>{initial}</div>;
}

export default function Navbar() {
  const { user, logout } = useAuth();
  const { language, setLanguage, copy } = useLanguage();
  const { theme, setTheme, themeOptions } = useTheme();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [online, setOnline] = useState(true);
  const [unread, setUnread] = useState(0);
  const [keyword, setKeyword] = useState('');
  const [openPath, setOpenPath] = useState([]);
  const [isMobileNav, setIsMobileNav] = useState(() => window.innerWidth < 768);
  const menuRef = useRef(null);
  const t = copy.nav;
  const navItems = createMenuConfig(t, Boolean(user), language);

  useEffect(() => {
    const handleResize = () => {
      const nextIsMobile = window.innerWidth < 768;
      setIsMobileNav(nextIsMobile);
      setOpenPath([]);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (!user) return;
    const fetch = () => {
      api.get('/notifications').then(res => setUnread(res.data.unread)).catch(() => {});
    };
    fetch();
    const timer = setInterval(fetch, 30000);
    return () => clearInterval(timer);
  }, [user]);

  useEffect(() => {
    if (!menuOpen) return undefined;

    const handlePointerDown = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setMenuOpen(false);
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') setMenuOpen(false);
    };

    document.addEventListener('pointerdown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [menuOpen]);

  const handleLogout = () => {
    logout();
    setMenuOpen(false);
    navigate('/');
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (keyword.trim()) navigate(`/services?keyword=${encodeURIComponent(keyword.trim())}`);
  };

  if (!user) {
    return (
      <nav className="navbar public-navbar">
        <div className="navbar-inner public-navbar-inner">
          <Link to="/" className="navbar-brand public-wordmark" aria-label={language === 'zh' ? '首页' : 'Home'}>
            <span className="brand-text">Oblitrok</span>
          </Link>

          <div className="navbar-links public-nav-links">
            {navItems.map(item => (
              <NavMenuItem
                key={item.label}
                item={item}
                isMobile={isMobileNav}
                openPath={openPath}
                setOpenPath={setOpenPath}
              />
            ))}
          </div>

          <div className="navbar-right public-actions">
            <form className="public-search" onSubmit={handleSearch}>
              <button type="submit" aria-label={t.search}>
                <Search size={18} strokeWidth={2.2} aria-hidden="true" />
              </button>
              <input
                value={keyword}
                onChange={e => setKeyword(e.target.value)}
                placeholder={t.search}
              />
            </form>
            <div className="language-switch" aria-label={t.language}>
              <button type="button" className={language === 'en' ? 'active' : ''} onClick={() => setLanguage('en')}>EN</button>
              <button type="button" className={language === 'zh' ? 'active' : ''} onClick={() => setLanguage('zh')}>中文</button>
            </div>
            <Link to="/login" className="public-login">{t.login}</Link>
            <Link to="/register" className="public-signup">{t.signup}</Link>
          </div>
        </div>
      </nav>
    );
  }

  return (
    <nav className="navbar upwork-topbar">
      <div className="navbar-inner upwork-nav-inner">
        <Link to="/" className="navbar-brand upwork-wordmark" aria-label={language === 'zh' ? '首页' : 'Home'}>
          <span className="brand-text">Oblitrok</span>
        </Link>

        <div className="navbar-links upwork-primary-nav">
          {navItems.map(item => (
            <NavMenuItem
              key={item.label}
              item={item}
              isMobile={isMobileNav}
              openPath={openPath}
              setOpenPath={setOpenPath}
            />
          ))}
        </div>

        <div className="navbar-right upwork-right">
          <form className="navbar-search upwork-search" onSubmit={handleSearch}>
            <button type="submit" className="search-icon" aria-label={t.search}>
              <Search size={18} strokeWidth={2.2} aria-hidden="true" />
            </button>
            <input
              type="text"
              placeholder={t.search}
              value={keyword}
              onChange={e => setKeyword(e.target.value)}
            />
            <span className="search-divider" />
            <Link to="/services" className="search-kind">{t.findServices || t.jobs} <span className="nav-caret" aria-hidden="true" /></Link>
          </form>

          {user ? (
            <>
              <div className="language-switch language-switch-light top-language-switch" aria-label={t.language}>
                <button type="button" className={language === 'en' ? 'active' : ''} onClick={() => setLanguage('en')}>EN</button>
                <button type="button" className={language === 'zh' ? 'active' : ''} onClick={() => setLanguage('zh')}>中文</button>
              </div>
              <Link to="/help" className="top-icon" title={t.help}>?</Link>
              <Link to="/notifications" className="top-icon notif-btn" title={t.notifications} onClick={() => setUnread(0)}>
                <span className="bell-icon" />
                {unread > 0 && <span className="notif-badge">{unread > 99 ? '99+' : unread}</span>}
              </Link>
              <div ref={menuRef} className="user-menu upwork-user-menu" onClick={() => setMenuOpen(!menuOpen)}>
                <UserAvatar user={user} className="avatar upwork-avatar" />
                {menuOpen && (
                  <div className="dropdown account-panel">
                    <div className="account-head">
                      <UserAvatar user={user} className="avatar account-avatar" />
                      <div className="account-identity">
                        <strong>{user.username || 'Bai Lao'}</strong>
                        <span>{t.freelancer}</span>
                      </div>
                    </div>
                    <button
                      className="account-online"
                      type="button"
                      onClick={e => {
                        e.stopPropagation();
                        setOnline(value => !value);
                      }}
                    >
                      <span>{t.onlineForMessages}</span>
                      <span className={`online-switch ${online ? 'on' : ''}`} />
                    </button>
                    <div className="account-group">
                      <Link to="/profile" className="dropdown-item" onClick={() => setMenuOpen(false)}>
                        <span className="menu-icon">◎</span> {t.yourProfile}
                      </Link>
                      <Link to="/dashboard" className="dropdown-item" onClick={() => setMenuOpen(false)}>
                        <span className="menu-icon">↗</span> {t.statsAndTrends}
                      </Link>
                    </div>
                    <div className="account-group">
                      <Link to="/reviews" className="dropdown-item" onClick={() => setMenuOpen(false)}>
                        <span className="menu-icon">◷</span> {t.accountHealth}
                      </Link>
                      <Link to="/wallet" className="dropdown-item" onClick={() => setMenuOpen(false)}>
                        <span className="menu-icon">¥</span> {t.connects}
                      </Link>
                      <div className="theme-section">
                        <div className="theme-section-label">{t.theme}</div>
                        <div className="theme-options" role="group" aria-label={t.theme}>
                          {themeOptions.map(themeKey => (
                            <button
                              key={themeKey}
                              className={`theme-option ${theme === themeKey ? 'active' : ''}`}
                              type="button"
                              aria-pressed={theme === themeKey}
                              onClick={e => {
                                e.stopPropagation();
                                setTheme(themeKey);
                                setMenuOpen(false);
                              }}
                            >
                              <span className="theme-swatch" style={{ '--theme-swatch-color': THEME_SWATCHES[themeKey] }} aria-hidden="true" />
                              <span className="theme-option-label">{THEME_LABELS[language][themeKey]}</span>
                              {theme === themeKey && <span className="theme-option-check" aria-hidden="true">✓</span>}
                            </button>
                          ))}
                        </div>
                      </div>
                      <Link to="/settings" className="dropdown-item" onClick={() => setMenuOpen(false)}>
                        <span className="menu-icon">⚙</span> {t.accountSettings}
                      </Link>
                    </div>
                    <button className="dropdown-item logout-item" onClick={handleLogout}>
                      <span className="menu-icon">↩</span> {t.logout}
                    </button>
                  </div>
                )}
              </div>
            </>
          ) : (
            <>
              <Link to="/help" className="top-icon" title={t.help}>?</Link>
              <Link to="/login" className="top-icon notif-btn" title={t.notifications}>
                <span className="bell-icon" />
              </Link>
              <Link to="/login" className="avatar upwork-avatar" title={t.login}>M</Link>
            </>
          )}
        </div>
      </div>
    </nav>
  );
}
